// TechnologyTransformation.js

import React, { useEffect } from "react";
import { Container, Box, Typography, Grid, Button, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import TransformIcon from "@mui/icons-material/Transform";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import TimelineIcon from "@mui/icons-material/Timeline";
import SpeedIcon from "@mui/icons-material/Speed";

export default function TechnologyTransformation() {
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const DARK_NAVY = "#0A192F";
  const LIGHT_GRAY = "#B0BEC5";
  const PRIMARY_GREEN = theme.palette.success.main;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const pillars = [
    {
      icon: <TransformIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Legacy Modernization",
      description:
        "Re‑architect monolithic, outdated systems into modular, scalable, and maintainable platforms without disrupting operations.",
    },
    {
      icon: <CloudDoneIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Cloud Migration",
      description:
        "Move your infrastructure, data, and workloads to the cloud with a structured, low‑risk, and cost‑efficient approach.",
    },
    {
      icon: <AutoAwesomeMotionIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Workflow Automation",
      description:
        "Replace manual, repetitive processes with intelligent automation that saves time and reduces operational errors.",
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Data & Insights",
      description:
        "Make better decisions with integrated data pipelines, dashboards, and analytics aligned with your business goals.",
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Performance & Reliability",
      description:
        "Optimize performance, uptime, and user experience across your digital touchpoints as your business scales.",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 8, md: 12 },
        backgroundColor: DARK_NAVY,
        color: "white",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={6} alignItems="center">
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "2.3rem", sm: "3rem", md: "3.3rem" },
                    mb: 2,
                    lineHeight: 1.2,
                  }}
                >
                  Technology <Box component="span" sx={{ color: PRIMARY_GREEN }}>Transformation</Box> for
                  modern businesses.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h6" sx={{ color: LIGHT_GRAY, fontWeight: 300, mb: 3 }}>
                  We help organizations evolve from legacy, fragmented systems to unified, cloud‑native,
                  and automation‑driven digital ecosystems.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body1" sx={{ color: LIGHT_GRAY, mb: 4, fontWeight: 300 }}>
                  Whether you are a growing startup or an established enterprise, we work with your teams
                  to redesign technology foundations so they support the next decade of growth — not just
                  the next release.
                </Typography>
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={{ display: "flex", gap: theme.spacing(2), flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/contact")}
                >
                  Discuss Transformation
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/services")}
                >
                  View All Services
                </Button>
              </motion.div>
            </Grid>

            {/* RIGHT CONTENT: PILLARS */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {pillars.map((pillar) => (
                  <Grid item xs={12} sm={6} key={pillar.title}>
                    <motion.div
                      variants={itemVariants}
                      style={{
                        background: "rgba(15, 23, 42, 0.9)",
                        borderRadius: 16,
                        padding: "18px 16px",
                        border: "1px solid rgba(148, 163, 184, 0.25)",
                        height: "100%",
                      }}
                    >
                      {pillar.icon}
                      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
                        {pillar.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: LIGHT_GRAY, fontSize: 13 }}>
                        {pillar.description}
                      </Typography>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}